import { useCallback, useEffect, useRef, useState, type ReactNode } from "react";

import type { Transport } from "@tdelivr/api-client";
import { services } from "@tdelivr/api-client";

import type { Session } from "../session.ts";

/**
 * The header kit every console page carries.
 *
 * Design rule 3 names four things and every console file in the bundle has all four: breadcrumbs, a
 * help popover with page tips, a notifications bell with a count and a dropdown, and an account menu
 * on the user's initials. The rules also say not to remove the header kit, which is worth taking
 * literally — it is the part of a console that is easiest to leave for later and never build.
 *
 * Every measurement here is read from `hdrUI()` in `02 Console Shell v3.dc.html`: 38px square buttons
 * with a 10px radius, a 320px popover 46px below the bar with a 14px radius, the account rows 38px
 * high with 13.5px type, and the unread badge at -5px from the top right corner with a 2px ring in the
 * surface colour so it reads as lifted off the button rather than clipped to it.
 *
 * One popover at a time, as the design's own toggle does: opening one closes the others. Escape closes
 * whatever is open and a click outside does the same, neither of which the design file needed to handle
 * because it is a static reference rather than something an operator uses for a whole shift.
 */

const inbox = services.notifications__in_app_notification;

/**
 * The scope the bell spends, named here because naming it is what grants it.
 *
 * `tooling/scripts/lib/channel-scopes.mjs` derives each client's realm grant from **string literals
 * in the application's own source**, intersected with the published scope vocabulary — read from the
 * TypeScript AST, so a scope named only in a comment or a sentence does not count. Its docblock
 * states the limit plainly: "a screen that spends a scope without naming it — calling an operation
 * through a generated client and never mentioning the permission in its own source — contributes
 * nothing here, and the token would be short that scope."
 *
 * **The bell was exactly that, on every page of all three consoles.** It calls
 * `listInAppNotifications` through the generated client and named no permission, so the derivation
 * saw nothing, neither realm granted `in-app-notifications:read`, and the inbox answered 403 for
 * every operator on every screen. Nothing caught it: the `catch` below is deliberate — a bell must
 * not take a board down — so the failure looked like an unreachable service rather than a missing
 * grant. Found by an adversarial review on 10 October, which also noted this was the only refusal on
 * these consoles that did not name its scope.
 *
 * The two existing bounds do not reach here, and that is why this is a constant rather than a note:
 * each console's `tests/page-routes.test.ts` checks that a *page's* declared requirements open one of
 * its owners, and the header kit is not a page.
 */
const INBOX_SCOPE = "in-app-notifications:read";

/**
 * How many notifications the dropdown shows. The badge counts the inbox, not this page.
 *
 * A string because every query parameter reaches the wire as one, and the generated client types them
 * that way rather than converting on the caller’s behalf.
 */
const DROPDOWN_LIMIT = "10";

export interface HeaderKitProps {
  readonly session: Session;
  readonly helpTitle: string;
  /** Page tips. The design supplies shell-level tips and no per-page text exists yet; see Q-03. */
  readonly tips: readonly string[];
  readonly transport: Transport;
  readonly onSignOut?: () => void;
  /** Opens and closes the drawer navigation, below 640 where the rail leaves the layout. */
  readonly onToggleNav?: () => void;
  readonly navOpen?: boolean;
}

type OpenPanel = "help" | "bell" | "account" | undefined;

interface InboxNotification {
  readonly notificationId: string;
  readonly title: string;
  readonly body: string;
  readonly state: string;
  readonly occurredAt: string;
}

/**
 * The account menu, exactly as the design lists it, in the design's order.
 *
 * Sign out is last, carries the danger foreground and a rule above it, because it is the one row whose
 * effect an operator cannot undo by clicking again.
 */
const ACCOUNT_ITEMS = [
  { icon: "person", label: "My account" },
  { icon: "badge", label: "Profile" },
  { icon: "tune", label: "Preferences" },
  { icon: "keyboard", label: "Keyboard shortcuts" },
  { icon: "logout", label: "Sign out" },
] as const;

/** "2 min ago". The design writes relative times on notification rows rather than clock times. */
function relative(occurredAt: string, now: number): string {
  const seconds = Math.max(0, Math.round((now - Date.parse(occurredAt)) / 1000));
  if (Number.isNaN(seconds)) return "";
  if (seconds < 60) return "just now";
  const minutes = Math.round(seconds / 60);
  if (minutes < 60) return `${minutes} min ago`;
  const hours = Math.round(minutes / 60);
  if (hours < 24) return `${hours} h ago`;
  return `${Math.round(hours / 24)} d ago`;
}

export function HeaderKit({
  session,
  helpTitle,
  tips,
  transport,
  onSignOut,
  onToggleNav,
  navOpen,
}: HeaderKitProps): ReactNode {
  const [open, setOpen] = useState<OpenPanel>(undefined);
  const [notifications, setNotifications] = useState<readonly InboxNotification[] | undefined>(
    undefined,
  );
  const [unreadCount, setUnreadCount] = useState<number | undefined>(undefined);
  const [inboxFailed, setInboxFailed] = useState(false);
  const region = useRef<HTMLDivElement>(null);

  const toggle = useCallback((panel: Exclude<OpenPanel, undefined>) => {
    setOpen((current) => (current === panel ? undefined : panel));
  }, []);

  /**
   * The inbox, read once on mount for the badge and again whenever the dropdown opens.
   *
   * The count is the service's own, not a count of these rows: a page of ten would say "9" when forty
   * are waiting, and the number on the bell is the reason somebody opens it.
   */
  const load = useCallback(async () => {
    if (session.userId === undefined) return;
    if (!session.scopes.has(INBOX_SCOPE)) {
      // No copy is invented for this: the design draws one unreachable-inbox state and this is
      // it. Whether a scope refusal should say so is Q-172, recorded rather than guessed at.
      setInboxFailed(true);
      setUnreadCount(undefined);
      return;
    }
    try {
      const client = inbox.createInAppNotificationClient(transport);
      const page = await client.listInAppNotifications(
        session.tenantId,
        session.userId,
        undefined,
        DROPDOWN_LIMIT,
      );
      setNotifications(page.items);
      setUnreadCount(page.unreadCount);
      setInboxFailed(false);
    } catch {
      // A header that cannot reach the inbox says so in the dropdown and shows no badge. It must not
      // take the page down with it: the bell is the least important thing on any console screen, and
      // an operator working a board does not need its failure.
      setInboxFailed(true);
      setUnreadCount(undefined);
    }
  }, [transport, session.tenantId, session.userId]);

  useEffect(() => {
    void load();
  }, [load]);

  useEffect(() => {
    if (open === "bell") void load();
  }, [open, load]);

  // Escape closes whatever is open, and a click anywhere outside the header does the same.
  useEffect(() => {
    if (open === undefined) return;
    const onKey = (event: KeyboardEvent): void => {
      if (event.key === "Escape") setOpen(undefined);
    };
    const onPointer = (event: MouseEvent): void => {
      if (!region.current?.contains(event.target as Node)) setOpen(undefined);
    };
    document.addEventListener("keydown", onKey);
    document.addEventListener("mousedown", onPointer);
    return (): void => {
      document.removeEventListener("keydown", onKey);
      document.removeEventListener("mousedown", onPointer);
    };
  }, [open]);

  const now = Date.now();
  const badge = unreadCount !== undefined && unreadCount > 0;

  return (
    <div className="hdr" ref={region}>
      {onToggleNav === undefined ? null : (
        // Below 640 the design's rail leaves the layout and its top bar grows a menu button. CSS
        // hides this above that width, because the width decides it and the design measures the
        // width. 640 rather than 900: four of the six console design files put it there and two put
        // it at 900, and between the two readings the majority still draws the 76px icon rail — so
        // a menu button at 900 would sit beside a rail that is already on screen. Recorded as Q-08.
        <button
          type="button"
          className="hdr__menu"
          aria-expanded={navOpen === true}
          aria-label={navOpen === true ? "Close menu" : "Open menu"}
          onClick={onToggleNav}
        >
          <span aria-hidden="true" className="hdr__glyph">
            menu
          </span>
        </button>
      )}
      {/*
        No breadcrumbs here. The reference screenshots put them in the page body above its heading,
        and neither console design renders the shell design's own `crumbBar` — so a top bar carrying
        them would be showing them twice, which it briefly did. `PageHeading` is the one place.

        That leaves the left of the bar to the tenant, which is what the screenshots show and what
        this console is required to state at all times.
      */}
      <div className="hdr__tenant">
        <span className="hdr__tenant-name">{session.tenantName}</span>
        {session.crossTenant ? (
          <span className="hdr__tenant-note" role="note">
            Acting across tenants
          </span>
        ) : null}
      </div>

      <div className="hdr__kit">
        <button
          type="button"
          className="hdr__icon"
          aria-expanded={open === "help"}
          aria-haspopup="dialog"
          /*
           * `Help for this page`, which is the words all seven design files put on this control.
           * It said `Page help`, which is the same meaning in fewer words and is not what the
           * design says — and an `aria-label` is copy, because it is what a screen reader reads out
           * loud. Section 1 keeps copy word for word and makes no exception for the copy only some
           * people hear.
           */
          aria-label="Help for this page"
          onClick={() => toggle("help")}
        >
          <span aria-hidden="true" className="hdr__glyph">
            help
          </span>
        </button>

        <div className="hdr__slot">
          <button
            type="button"
            className="hdr__icon"
            aria-expanded={open === "bell"}
            aria-haspopup="dialog"
            aria-label={
              unreadCount === undefined ? "Notifications" : `Notifications, ${unreadCount} unread`
            }
            onClick={() => toggle("bell")}
          >
            <span aria-hidden="true" className="hdr__glyph">
              notifications
            </span>
            {badge ? (
              // The number is not repeated to a screen reader: the button's own label carries it, and a
              // badge announced separately reads as a stray digit.
              <span aria-hidden="true" className="hdr__badge">
                {unreadCount > 99 ? "99+" : unreadCount}
              </span>
            ) : null}
          </button>
        </div>

        <div className="hdr__slot">
          <button
            type="button"
            className="hdr__avatar"
            aria-expanded={open === "account"}
            aria-haspopup="menu"
            /*
             * `Account menu`, as all seven design files have it, rather than the operator's name
             * appended to it.
             *
             * Appending the name was a deliberate improvement and it is still a deviation. There is
             * one account menu on the page, so the name disambiguates nothing; the panel it opens
             * names the operator in its first row, which is where the design puts that information.
             */
            aria-label="Account menu"
            onClick={() => toggle("account")}
          >
            {session.initials}
          </button>
        </div>

        {open === "help" ? (
          <div className="hdr__pop" role="dialog" aria-label={`Help: ${helpTitle}`}>
            <p className="hdr__pop-title">{helpTitle}</p>
            <ul className="hdr__tips">
              {tips.map((tip) => (
                <li key={tip}>{tip}</li>
              ))}
            </ul>
          </div>
        ) : null}

        {open === "bell" ? (
          <div className="hdr__pop" role="dialog" aria-label="Notifications">
            <p className="hdr__pop-title">Notifications</p>
            {inboxFailed ? (
              <p className="hdr__pop-empty">
                The console cannot reach your notifications right now. Everything else on this page
                is unaffected.
              </p>
            ) : notifications === undefined ? (
              <p className="hdr__pop-empty">Loading</p>
            ) : notifications.length === 0 ? (
              <p className="hdr__pop-empty">Nothing for you yet.</p>
            ) : (
              <ul className="hdr__notifs">
                {notifications.map((notification) => (
                  <li
                    key={notification.notificationId}
                    className={
                      notification.state === "unread"
                        ? "hdr__notif hdr__notif--unread"
                        : "hdr__notif"
                    }
                  >
                    {/*
                      One glyph for every row. The design's three sample rows each carry a different
                      icon and colour, but they are sample data: the design defines no mapping from a
                      notification's category to an icon, and no service ingests a notification yet, so
                      there is no category vocabulary to map. Recorded as Q-04 rather than invented.
                    */}
                    <span aria-hidden="true" className="hdr__notif-glyph">
                      notifications
                    </span>
                    <span className="hdr__notif-text">
                      <span className="hdr__notif-title">{notification.title}</span>
                      <span className="hdr__notif-when">
                        {relative(notification.occurredAt, now)}
                      </span>
                    </span>
                  </li>
                ))}
              </ul>
            )}
          </div>
        ) : null}

        {open === "account" ? (
          <div className="hdr__pop" role="menu" aria-label="Account">
            {/*
              The design's identity block shows a name, a role and an email address. The token carries
              the name; it carries no role and no email — the contract is `sub`, `jti`, `scope[]` and
              optionally `tenant_id`, `sid` and `aal` — so those two lines have no source and are not
              filled with something plausible. The tenant takes the second line instead, which is the
              one thing this console is required to say at all times: whose data you are looking at.
              Recorded as Q-05.
            */}
            <p className="hdr__who">
              <span className="hdr__who-name">{session.displayName}</span>
              <span className="hdr__who-tenant">{session.tenantName}</span>
            </p>
            <ul className="hdr__acct">
              {ACCOUNT_ITEMS.map((item) => (
                <li key={item.label}>
                  <button
                    type="button"
                    role="menuitem"
                    className={
                      item.label === "Sign out"
                        ? "hdr__acct-row hdr__acct-row--out"
                        : "hdr__acct-row"
                    }
                    onClick={() => {
                      setOpen(undefined);
                      if (item.label === "Sign out") onSignOut?.();
                    }}
                  >
                    <span aria-hidden="true" className="hdr__acct-glyph">
                      {item.icon}
                    </span>
                    {item.label}
                  </button>
                </li>
              ))}
            </ul>
          </div>
        ) : null}
      </div>
    </div>
  );
}
